/**
 * CESM OSINT - Panel SOCMINT (Social Media Intelligence)
 * Affiche les sources configurées + fil Reddit live
 */
import React, { useState, useEffect } from 'react';
import {
  Twitter, MessageCircle, Users, Globe, ExternalLink,
  ChevronDown, ChevronUp, Search, RefreshCw,
  Radio, Linkedin, Video, Rss, ArrowUpRight, Clock,
  BarChart3, TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

const PLATFORM_ICONS = {
  twitter: Twitter, reddit: MessageCircle, telegram: Radio,
  vk: Users, tiktok: Video, linkedin: Linkedin,
};
const PLATFORM_COLORS = {
  twitter: 'text-sky-400 border-sky-500/50 bg-sky-500/10',
  reddit: 'text-orange-400 border-orange-500/50 bg-orange-500/10',
  telegram: 'text-blue-400 border-blue-500/50 bg-blue-500/10',
  vk: 'text-indigo-400 border-indigo-500/50 bg-indigo-500/10',
  tiktok: 'text-pink-400 border-pink-500/50 bg-pink-500/10',
  linkedin: 'text-blue-500 border-blue-600/50 bg-blue-600/10',
};

const SOCMINTPanel = ({ token }) => {
  const [sources, setSources] = useState(null);
  const [redditFeed, setRedditFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    if (token) {
      fetchSources();
      fetchRedditFeed();
    }
  }, [token]);

  const fetchSources = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/socmint/sources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.status === 'success') setSources(res.data);
    } catch (err) {
      console.error('SOCMINT sources error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRedditFeed = async () => {
    try {
      const res = await axios.get(`${API}/socmint/reddit/feed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.status === 'success') setRedditFeed(res.data);
    } catch (err) {
      console.error('Reddit feed error:', err);
    }
  };

  const refreshReddit = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/socmint/reddit/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.status === 'success') {
        setRedditFeed(res.data);
        toast.success(`${res.data.total_posts} posts collectes`);
      }
    } catch (err) {
      toast.error('Erreur rafraichissement Reddit');
    } finally {
      setRefreshing(false);
    }
  };

  const togglePlatform = (p) => {
    setExpandedPlatform(expandedPlatform === p ? null : p);
    setExpandedCategory(null);
  };
  const toggleCategory = (c) => setExpandedCategory(expandedCategory === c ? null : c);

  const filterAccounts = (accounts) => {
    if (!searchTerm) return accounts;
    const t = searchTerm.toLowerCase();
    return accounts.filter(a =>
      (a.handle || a.subreddit || a.name || a.id || '').toLowerCase().includes(t) ||
      (a.focus || '').toLowerCase().includes(t)
    );
  };

  if (loading) {
    return (
      <div className="p-6 text-center" data-testid="socmint-loading">
        <RefreshCw className="w-8 h-8 text-marine-cyan animate-spin mx-auto mb-4" />
        <p className="font-mono text-white/70">Chargement SOCMINT...</p>
      </div>
    );
  }

  const counts = sources?.counts || {};
  const data = sources?.sources || {};

  return (
    <div className="space-y-6" data-testid="socmint-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Globe className="w-7 h-7 text-marine-cyan" />
          <div>
            <h2 className="font-heading text-marine-cyan text-xl uppercase tracking-wider">
              SOCMINT
            </h2>
            <p className="font-mono text-white/60 text-xs">
              {counts.total || 0} sources — Collecte Reddit active
            </p>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher..."
            data-testid="socmint-search"
            className="pl-10 pr-4 py-2 bg-black/40 border border-marine-slate/40 rounded font-mono text-sm text-white placeholder:text-white/30 w-56"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-marine-slate/30 pb-px">
        {[
          { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
          { id: 'feed', label: 'Fil Reddit', icon: Rss },
          { id: 'sources', label: 'Sources', icon: Globe },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`socmint-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm transition-all border-b-2 ${
              activeTab === tab.id
                ? 'border-marine-cyan text-marine-cyan'
                : 'border-transparent text-white/50 hover:text-white/80'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* === TAB: Dashboard === */}
      {activeTab === 'dashboard' && (
        <SOCMINTDashboard redditFeed={redditFeed} sources={sources} refreshReddit={refreshReddit} refreshing={refreshing} />
      )}

      {/* === TAB: Reddit Feed === */}
      {activeTab === 'feed' && (
        <div className="space-y-4">
          {/* Feed header */}
          <div className="flex items-center justify-between">
            <div className="font-mono text-white/60 text-xs flex items-center gap-2">
              <Clock className="w-3 h-3" />
              {redditFeed?.scraped_at
                ? `Mis a jour: ${new Date(redditFeed.scraped_at).toLocaleString('fr-FR')}`
                : 'Aucune collecte'}
              {redditFeed && (
                <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded text-xs">
                  {redditFeed.total_posts} posts
                </span>
              )}
            </div>
            <button
              onClick={refreshReddit}
              disabled={refreshing}
              data-testid="socmint-refresh-reddit"
              className="flex items-center gap-2 px-3 py-1.5 bg-orange-500/20 text-orange-400 border border-orange-500/40 rounded font-mono text-xs hover:bg-orange-500/30 transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Collecte...' : 'Rafraichir'}
            </button>
          </div>

          {/* Posts list */}
          {!redditFeed || redditFeed.total_posts === 0 ? (
            <div className="p-8 text-center border border-dashed border-marine-slate/30 rounded-lg">
              <Rss className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="font-mono text-white/50 text-sm mb-3">Aucun post collecte</p>
              <button
                onClick={refreshReddit}
                disabled={refreshing}
                className="px-4 py-2 bg-orange-500/20 text-orange-400 border border-orange-500/40 rounded font-mono text-xs hover:bg-orange-500/30"
              >
                Lancer la collecte Reddit
              </button>
            </div>
          ) : (
            <div className="grid gap-2 max-h-[600px] overflow-y-auto pr-1">
              {redditFeed.posts.map((post, idx) => (
                <a
                  key={post.id || idx}
                  href={post.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`reddit-post-${idx}`}
                  className="flex items-start gap-3 p-3 bg-black/30 border border-orange-500/10 rounded hover:border-orange-500/40 hover:bg-orange-500/5 transition-all group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 text-[10px] font-mono rounded">
                        r/{post.subreddit}
                      </span>
                      <span className="text-white/40 text-[10px] font-mono">{post.author}</span>
                      {post.published && (
                        <span className="text-white/30 text-[10px] font-mono">
                          {new Date(post.published).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                    <p className="text-white/90 text-sm leading-snug line-clamp-2 group-hover:text-orange-300 transition-colors">
                      {post.title}
                    </p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-white/20 group-hover:text-orange-400 flex-shrink-0 mt-1" />
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {/* === TAB: Sources === */}
      {activeTab === 'sources' && (
        <div className="space-y-4">
          {/* Platform cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(counts).filter(([k]) => k !== 'total').map(([platform, count]) => {
              const Icon = PLATFORM_ICONS[platform] || Globe;
              const colors = PLATFORM_COLORS[platform] || 'text-white border-white/30';
              return (
                <button
                  key={platform}
                  onClick={() => togglePlatform(platform)}
                  data-testid={`socmint-platform-${platform}`}
                  className={`p-3 border rounded transition-all ${colors} ${
                    expandedPlatform === platform ? 'ring-2 ring-white/30' : 'hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-5 h-5 mx-auto mb-1" />
                  <p className="font-mono text-xs uppercase">{platform}</p>
                  <p className="font-bold text-lg">{count}</p>
                </button>
              );
            })}
          </div>

          {/* Expanded platform detail */}
          {expandedPlatform && data[expandedPlatform] && (
            <PlatformDetail
              platform={expandedPlatform}
              data={data[expandedPlatform]}
              expandedCategory={expandedCategory}
              toggleCategory={toggleCategory}
              filterAccounts={filterAccounts}
            />
          )}
        </div>
      )}

      {/* Footer */}
      <div className="p-3 bg-marine-slate/10 border border-marine-slate/20 rounded">
        <p className="font-mono text-white/40 text-[10px]">
          <strong className="text-marine-cyan">SOCMINT</strong> — Reddit: collecte live via RSS.
          Twitter/Telegram/VK: sources referencees (API requises).
        </p>
      </div>
    </div>
  );
};


/* ---------- Dashboard Component ---------- */

const SOCMINTDashboard = ({ redditFeed, sources, refreshReddit, refreshing }) => {
  const posts = redditFeed?.posts || [];
  const counts = sources?.counts || {};

  // Stats par subreddit
  const subStats = {};
  posts.forEach(p => {
    const sub = p.subreddit || 'unknown';
    if (!subStats[sub]) subStats[sub] = { count: 0, authors: new Set() };
    subStats[sub].count++;
    if (p.author) subStats[sub].authors.add(p.author);
  });
  const topSubs = Object.entries(subStats)
    .map(([name, s]) => ({ name, count: s.count, authors: s.authors.size }))
    .sort((a, b) => b.authors - a.authors);

  const maxAuthors = Math.max(...topSubs.map(s => s.authors), 1);
  const maxCount = Math.max(...topSubs.map(s => s.count), 1);

  // Posts récents (dernières 24h)
  const now = Date.now();
  const recent = posts.filter(p => {
    try { return now - new Date(p.published).getTime() < 86400000; } catch { return false; }
  });

  // Mots fréquents dans les titres
  const wordFreq = {};
  const stopWords = new Set(['the','a','an','in','on','at','to','for','of','is','and','or','by','with','from','as','it','its','that','this','has','have','are','was','were','been','be','not','but','will','can','all','more','no','about','after','new','over','us','up','out','how','why','what','their']);
  posts.forEach(p => {
    (p.title || '').split(/\s+/).forEach(w => {
      const clean = w.toLowerCase().replace(/[^a-z]/g, '');
      if (clean.length > 3 && !stopWords.has(clean)) {
        wordFreq[clean] = (wordFreq[clean] || 0) + 1;
      }
    });
  });
  const topWords = Object.entries(wordFreq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12);
  const maxWordFreq = Math.max(...topWords.map(w => w[1]), 1);

  return (
    <div className="space-y-5" data-testid="socmint-dashboard">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Posts collectes', value: posts.length, color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/30' },
          { label: 'Subreddits actifs', value: topSubs.length, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30' },
          { label: 'Dernières 24h', value: recent.length, color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/30' },
          { label: 'Sources totales', value: counts.total || 0, color: 'text-marine-cyan', bg: 'bg-marine-cyan/10 border-marine-cyan/30' },
        ].map((kpi, i) => (
          <div key={i} className={`p-3 border rounded ${kpi.bg}`} data-testid={`socmint-kpi-${i}`}>
            <p className="font-mono text-white/50 text-[10px] uppercase">{kpi.label}</p>
            <p className={`font-heading text-2xl ${kpi.color}`}>{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Top Subreddits Bar Chart */}
      <div className="bg-black/30 border border-marine-slate/20 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-mono text-white/80 text-sm flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-orange-400" />
            Top Subreddits (posts / auteurs uniques)
          </h3>
          <button
            onClick={refreshReddit}
            disabled={refreshing}
            data-testid="dashboard-refresh-btn"
            className="px-2 py-1 text-[10px] font-mono text-orange-400 border border-orange-500/30 rounded hover:bg-orange-500/10"
          >
            <RefreshCw className={`w-3 h-3 inline mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Rafraichir
          </button>
        </div>
        {topSubs.length === 0 ? (
          <p className="text-white/30 font-mono text-xs text-center py-4">Aucune donnée — lancez une collecte</p>
        ) : (
          <div className="space-y-2">
            {topSubs.map((sub, i) => (
              <div key={sub.name} className="flex items-center gap-3" data-testid={`bar-${sub.name}`}>
                <span className="font-mono text-orange-400 text-xs w-28 truncate">r/{sub.name}</span>
                <div className="flex-1 bg-white/5 rounded-full h-5 overflow-hidden relative">
                  <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange-500/60 to-orange-400/40 rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(8, (sub.authors / maxAuthors) * 100)}%` }}
                  />
                  <span className="absolute right-2 top-0 h-full flex items-center font-mono text-[10px] text-white/90 z-10">{sub.authors} auteurs</span>
                </div>
                <span className="font-mono text-white/40 text-[10px] w-16 text-right">{sub.count} posts</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Trending Topics (word cloud style) */}
      <div className="bg-black/30 border border-marine-slate/20 rounded-lg p-4">
        <h3 className="font-mono text-white/80 text-sm flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-blue-400" />
          Sujets tendance
        </h3>
        {topWords.length === 0 ? (
          <p className="text-white/30 font-mono text-xs text-center py-4">Aucune donnée</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {topWords.map(([word, freq]) => {
              const intensity = freq / maxWordFreq;
              const size = intensity > 0.6 ? 'text-base' : intensity > 0.3 ? 'text-sm' : 'text-xs';
              const opacity = Math.max(0.4, intensity);
              return (
                <span
                  key={word}
                  className={`font-mono ${size} px-2 py-1 rounded border border-blue-500/20 bg-blue-500/5`}
                  style={{ color: `rgba(96, 165, 250, ${opacity})` }}
                  data-testid={`trend-${word}`}
                >
                  {word} <sup className="text-[9px] opacity-60">{freq}</sup>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Platform distribution */}
      <div className="bg-black/30 border border-marine-slate/20 rounded-lg p-4">
        <h3 className="font-mono text-white/80 text-sm mb-3">Plateformes configurées</h3>
        <div className="flex items-end gap-1 h-24">
          {Object.entries(counts).filter(([k]) => k !== 'total').map(([platform, count]) => {
            const max = Math.max(...Object.values(counts).filter((_, i) => i > 0), 1);
            const h = Math.max(8, (count / max) * 100);
            const colors = PLATFORM_COLORS[platform] || 'text-white';
            const textClass = colors.split(' ').find(c => c.startsWith('text-')) || 'text-white';
            return (
              <div key={platform} className="flex-1 flex flex-col items-center gap-1">
                <span className={`font-mono text-[10px] ${textClass}`}>{count}</span>
                <div
                  className="w-full rounded-t bg-gradient-to-t from-marine-slate/40 to-marine-cyan/30"
                  style={{ height: `${h}%` }}
                />
                <span className="font-mono text-[9px] text-white/40 uppercase">{platform}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};


/* ---------- Sub-component: Platform detail ---------- */

const PLATFORM_LINK = {
  twitter: (a) => `https://twitter.com/${(a.handle || '').replace('@', '')}`,
  reddit: (a) => `https://reddit.com/${a.subreddit}`,
  telegram: (a) => `https://t.me/${(a.handle || '').replace('@', '')}`,
  vk: (a) => `https://vk.com/${a.id}`,
};

const PlatformDetail = ({ platform, data, expandedCategory, toggleCategory, filterAccounts }) => {
  const color = PLATFORM_COLORS[platform] || '';
  const borderClass = color.split(' ').find(c => c.startsWith('border-')) || 'border-white/30';
  const bgClass = color.split(' ').find(c => c.startsWith('bg-')) || '';
  const textClass = color.split(' ').find(c => c.startsWith('text-')) || 'text-white';
  const Icon = PLATFORM_ICONS[platform] || Globe;
  const getLink = PLATFORM_LINK[platform] || (() => '#');

  return (
    <div className={`border ${borderClass} rounded-lg overflow-hidden`} data-testid={`socmint-detail-${platform}`}>
      <div className={`${bgClass} px-4 py-3 flex items-center gap-3`}>
        <Icon className={`w-5 h-5 ${textClass}`} />
        <h3 className={`font-heading ${textClass} uppercase`}>{platform}</h3>
      </div>
      <div className={`divide-y divide-white/5`}>
        {Object.entries(data).map(([category, accounts]) => {
          if (!Array.isArray(accounts)) return null;
          const filtered = filterAccounts(accounts);
          return (
            <div key={category}>
              <button
                onClick={() => toggleCategory(category)}
                className="w-full px-4 py-2 flex items-center justify-between hover:bg-white/5 transition-all"
              >
                <span className="font-mono text-white/80 text-sm capitalize">{category.replace(/_/g, ' ')}</span>
                <div className="flex items-center gap-2">
                  <span className={`font-mono ${textClass} text-xs`}>{filtered.length}</span>
                  {expandedCategory === category ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                </div>
              </button>
              {expandedCategory === category && (
                <div className="px-4 pb-3 grid gap-1">
                  {filtered.map((account, idx) => (
                    <a
                      key={idx}
                      href={getLink(account)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-2 bg-black/20 rounded hover:bg-white/5 transition-all group"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`font-mono ${textClass} text-sm`}>
                          {account.handle || account.subreddit || account.id || account.name}
                        </span>
                        <span className="text-white/50 text-xs">{account.name || ''}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-white/40 text-[10px]">{account.focus}</span>
                        <ExternalLink className="w-3 h-3 text-white/20 group-hover:text-white/60" />
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SOCMINTPanel;
